import type { ImportedPageDocument } from './import-types';

const ATTRIBUTE_PATTERN = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

function decodeAttributeValue(value: string): string {
  return value
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

export function parseAttributes(source = ''): Record<string, string> {
  const attributes: Record<string, string> = {};
  let match: RegExpExecArray | null;

  ATTRIBUTE_PATTERN.lastIndex = 0;
  while ((match = ATTRIBUTE_PATTERN.exec(source))) {
    const [, name, doubleQuoted, singleQuoted, unquoted] = match;
    if (!name) continue;
    const value = doubleQuoted ?? singleQuoted ?? unquoted ?? '';
    attributes[name.toLowerCase()] = decodeAttributeValue(value);
  }

  return attributes;
}

function firstMatch(source: string, pattern: RegExp): string {
  return pattern.exec(source)?.[1]?.trim() ?? '';
}

function allMatches(source: string, pattern: RegExp): string[] {
  return Array.from(source.matchAll(pattern), (match) => match[1]?.trim() ?? '').filter(Boolean);
}

function collectAttributeValues(source: string, attribute: 'src' | 'href' | 'poster'): string[] {
  const pattern = new RegExp(`\\b${attribute}\\s*=\\s*(?:"([^"]+)"|'([^']+)'|([^\\s>]+))`, 'gi');
  return Array.from(source.matchAll(pattern), (match) => match[1] ?? match[2] ?? match[3] ?? '')
    .map((value) => value.trim())
    .filter(Boolean);
}

function isLocalReference(value: string): boolean {
  return !/^(?:[a-z][a-z\d+.-]*:|\/\/|#|data:|mailto:|tel:|javascript:)/i.test(value);
}

export function routeFromSourcePath(sourcePath: string): string {
  const normalized = sourcePath.replace(/\\/g, '/').replace(/^\.\//, '');
  const isIndexDocument = /(?:^|\/)index\.html?$/i.test(normalized);
  const routePath = isIndexDocument
    ? normalized.replace(/(?:^|\/)index\.html?$/i, '')
    : normalized.replace(/\.html?$/i, '');
  const route = `/${routePath}`.replace(/\/{2,}/g, '/');
  return route === '/' ? '/' : `${route.replace(/\/$/, '')}/`;
}

export function parseImportedDocument(input: {
  sourcePath: string;
  sourceDocument: string;
}): ImportedPageDocument {
  const { sourcePath, sourceDocument } = input;
  const doctype = firstMatch(sourceDocument, /(<!doctype[^>]*>)/i);
  const htmlOpen = /<html\b([^>]*)>/i.exec(sourceDocument);
  const headHtml = firstMatch(sourceDocument, /<head\b[^>]*>([\s\S]*?)<\/head>/i);
  const bodyMatch = /<body\b([^>]*)>([\s\S]*?)<\/body>/i.exec(sourceDocument);
  const bodyHtml = bodyMatch?.[2] ?? sourceDocument;

  const title = firstMatch(headHtml, /<title\b[^>]*>([\s\S]*?)<\/title>/i);
  const descriptionTag = /<meta\b[^>]*\bname\s*=\s*(?:"description"|'description'|description)[^>]*>/i.exec(headHtml)?.[0] ?? '';
  const metaDescription = parseAttributes(descriptionTag.replace(/^<meta\b|\/?\s*>$/gi, '')).content ?? '';

  const inlineCss = allMatches(headHtml, /<style\b[^>]*>([\s\S]*?)<\/style>/gi);
  const linkedStylesheets = Array.from(headHtml.matchAll(/<link\b([^>]*)>/gi))
    .map((match) => parseAttributes(match[1]))
    .filter((attributes) => (attributes.rel ?? '').toLowerCase().split(/\s+/).includes('stylesheet'))
    .map((attributes) => attributes.href ?? '')
    .filter(Boolean);

  const linkedScripts = Array.from(sourceDocument.matchAll(/<script\b([^>]*)>[\s\S]*?<\/script>/gi))
    .map((match) => parseAttributes(match[1]).src ?? '')
    .filter(Boolean);
  const inlineScripts = Array.from(sourceDocument.matchAll(/<script\b([^>]*)>([\s\S]*?)<\/script>/gi))
    .filter((match) => !parseAttributes(match[1]).src)
    .map((match) => match[2]?.trim() ?? '')
    .filter(Boolean);

  const assetPaths = Array.from(
    new Set([
      ...collectAttributeValues(sourceDocument, 'src'),
      ...collectAttributeValues(sourceDocument, 'poster'),
      ...linkedStylesheets,
      ...linkedScripts,
      ...Array.from(sourceDocument.matchAll(/url\(\s*(?:"([^"]+)"|'([^']+)'|([^)'"\s]+))\s*\)/gi), (match) =>
        (match[1] ?? match[2] ?? match[3] ?? '').trim(),
      ),
    ].filter((value) => value && isLocalReference(value))),
  );

  return {
    sourcePath,
    route: routeFromSourcePath(sourcePath),
    sourceDocument,
    doctype,
    htmlAttributes: parseAttributes(htmlOpen?.[1] ?? ''),
    headHtml,
    bodyHtml,
    bodyAttributes: parseAttributes(bodyMatch?.[1] ?? ''),
    title,
    metaDescription,
    inlineCss,
    linkedStylesheets,
    linkedScripts,
    inlineScripts,
    assetPaths,
  };
}
